/**
 *
 * MobileBottomBar
 *
 */

import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';

// styles
import './style.scss';

// antd component
import { List, Drawer } from 'antd';
import { TabBar } from 'antd-mobile';

import history from 'utils/history';

// assets
import CustomIcons from 'components/CustomIcons';
import { MenuOutlined, MoreOutlined } from '@ant-design/icons';

// global user
import {
  withUser,
  useGetLocalUser,
  useGetLocalActiveChild,
  // parent
  useChildNotificatoin,
  useGetChildNotification,
  useUpdateChildNotifications,
  // school
  useSchoolNotificatoin,
  useGetSchoolNotification,
  useUpdateSchoolNotifications,
} from 'engine/Contexts/User.context';

// hooks
import { useRefState } from 'utils/hooks';
import { getObjId } from 'utils/helpers/helper.general';

function MobileBottomBar({
  selectedTab,
  // states
  user,
  // actions
  changeTab,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames('fixed-nav-mobile', className);

  const historyStates = history.location?.state;

  const localUser = useGetLocalUser()();
  const activeChild = useGetLocalActiveChild()();

  const childNotificatoin = useChildNotificatoin();
  const getChildNotification = useGetChildNotification();
  const updateChildNotifications = useUpdateChildNotifications();

  const schoolNotificatoin = useSchoolNotificatoin();
  const getSchoolNotification = useGetSchoolNotification();
  const updateSchoolNotifications = useUpdateSchoolNotifications();

  const location = useLocation();
  const nameMenuSplit = location.pathname.split('/');
  const dafaultPath = `/${nameMenuSplit[1]}`;

  const [notReadCommAmount, notReadCommAmountRef, setNotReadCommAmount] = useRefState(false);
  const [absAmount, setAbsAmount] = useState(false);

  const [defaultActiveItem, setDefaultActiveItem] = useState(dafaultPath);
  const [drawerOpened, setDrawerOpened] = useState(false);

  const getChildNotif = useCallback(async (childID, reset = false) => {  
    const notifs = childNotificatoin();
    const hasNotif = notifs?.find(el => getObjId(el) == getObjId(childID));
    if (!!hasNotif && !reset) {
      setNotReadCommAmount(hasNotif?.totalAmount);
      setAbsAmount(hasNotif?.absNeedActionsAmount);
    } else {
      const result = await getChildNotification(childID);
      setNotReadCommAmount(result?.totalAmount);
      setAbsAmount(result?.absNeedActionsAmount);
      updateChildNotifications({
        _id: childID,
        ...result
      });
    }
  }, [location.pathname]);

  const getSchoolNotif = useCallback(async (schoolID = '1', reset = false) => {
    const notifs = schoolNotificatoin();
    const hasNotif = notifs?.find(el => getObjId(el) == getObjId(schoolID));
    if (!!hasNotif && !reset) {
      setNotReadCommAmount(hasNotif?.totalAmount);
      setAbsAmount(hasNotif?.absNeedActionsAmount);
    } else {
      const result = await getSchoolNotification(schoolID);
     // console.log({ result })
      setNotReadCommAmount(result?.totalAmount);
      setAbsAmount(result?.absNeedActionsAmount);
      updateSchoolNotifications({
        _id: schoolID,
        ...result
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (localUser?.role == 'parent') {
      if (activeChild?._id) {
        getChildNotif(activeChild?._id);
      } else {
        setNotReadCommAmount(false);
        setAbsAmount(false);
      }
    } /* else if (localUser?.role == 'teacher') {
      getSchoolNotif('1', true);
    } */
  }, [activeChild?._id, user?.childsNotifications]);

  // Get and change path from location into bottom bar
  useEffect(() => {
    const newPath = `/${nameMenuSplit[1]}`;
    if (historyStates?.cameFrom == "single_communication") {
      if (localUser?.role == 'teacher') {
        getSchoolNotif('1', true);
      }
    }
    if (newPath !== defaultActiveItem) {
      setDefaultActiveItem(newPath);
      if (localUser?.role == 'teacher') {
        getSchoolNotif('1', true);
      }
    }
  }, [location.pathname]);

  // toggle drawers
  const openDrawer = useCallback(() => {
    setDrawerOpened(prev => !prev);
  }, []);

  const communicationItem = useMemo(() => {
    return (
      <TabBar.Item
        title="News"
        key="/communications"
        icon={
          <>
            {(selectedTab === '/communications' || selectedTab === '/' || selectedTab === '/home') ? (
              <CustomIcons type="letter-close-active" />
            ) : (
              <CustomIcons type="letter-close" />
            )}
          </>
        }
        badge={!!notReadCommAmountRef?.current && notReadCommAmountRef?.current}
        data-seed="logId"
      />
    );
  }, [notReadCommAmount, notReadCommAmountRef?.current, selectedTab, location.pathname]);

  const absencesItem = useMemo(() => {
    return (
      <TabBar.Item
        title="Absences"
        key="/absences"
        icon={
          <>
            {selectedTab === '/absences' ? (
              <CustomIcons type="absence-active" />
            ) : (
              <CustomIcons type="absence" />
            )}
          </>
        }
        badge={!!absAmount && absAmount}
        data-seed="logId"
      />
    );
  }, [localUser?.role, absAmount, selectedTab, defaultActiveItem, activeChild?._id]);

  const homeItem = useMemo(() => (
    <TabBar.Item
      title="Home"
      key="/home"
      icon={
        <>
          {selectedTab === '/home' ? (
            <CustomIcons className="home-icon" type="home-active" />
          ) : (
            <CustomIcons className="home-icon" type="home" />
          )}
        </>
      }
      // badge={1}
      data-seed="logId"
    />
  ), [selectedTab]);

  const schoolItem = useMemo(() => (
    localUser?.role == 'parent' && (
      <TabBar.Item
        title="Ecole"
        key="/ecole"
        icon={
          <>
            {selectedTab === '/ecole' ? (
              <CustomIcons className="school" type="school-active" />
            ) : (
              <CustomIcons className="school" type="school" />
            )}
          </>
        }
        // badge={1}
        data-seed="logId"
      />
    )
  ), [localUser?.role, selectedTab]);
  
  const coursItem = useMemo(() => (
    <TabBar.Item
      title="Agenda"
      key="/сours"
      icon={
        <>
          {selectedTab === '/сours' ? (
            <CustomIcons type="table-active" />
          ) : (
            <CustomIcons type="table" />
          )}
        </>
      }
      // badge={1}
      data-seed="logId"
    />
  ), [selectedTab]);
  
  const presencesItem = useMemo(() => (
    localUser?.role == 'teacher' && (
      <TabBar.Item
        title="Présences"
        key="/attendance"
        icon={
          <>
            {selectedTab === '/attendance' ? (
              <CustomIcons type="absence-checked-active" />
            ) : (
              <CustomIcons type="absence-checked" />
            )}
          </>
        }
        // badge={1}
        data-seed="logId"
      />
    )
  ), [localUser?.role, selectedTab]);

  const moreItems = useMemo(() => (
    <TabBar.Item
      title="Plus"
      key="/menu"
      icon={
        <>
          {selectedTab === '/menu' ? (
            <CustomIcons className="dots-horizontal" type="dots-vertical" />
          ) : (
            <CustomIcons className="dots-horizontal" type="dots-vertical" />
          )}
        </>
      }
    />
  ), [selectedTab, openDrawer]);

  const sideContent = useMemo(() => { 
    return (
      <List className="mobile-bottom-sidebar-menu">
        <List.Item
          className="mobile-bottom-sidebar-menu__item"
          key={'communications'}
        >
          <Link className="menu-item-link" to={`/communications`} onClick={() => setDrawerOpened(false)}>
            <CustomIcons className="menu-item-icon" type="letter-close" /> News
          </Link>
        </List.Item>

        <List.Item
          className="mobile-bottom-sidebar-menu__item"
          key={'absences'}
        >
          <Link className="menu-item-link" to={`/absences`} onClick={() => setDrawerOpened(false)}>
            <CustomIcons className="menu-item-icon" type="absence" /> Absences
          </Link>
        </List.Item>

        <List.Item
          className="mobile-bottom-sidebar-menu__item"
          key={'сours'}
        >
          <Link className="menu-item-link" to={`/сours`} onClick={() => setDrawerOpened(false)}>
            <CustomIcons className="menu-item-icon" type="table" /> Agenda
          </Link>
        </List.Item>
        
        {localUser?.role == 'teacher' && (
          <List.Item
            className="mobile-bottom-sidebar-menu__item"
            key={'attendance'}
          >
            <Link className="menu-item-link" to={`/attendance`} onClick={() => setDrawerOpened(false)}>
              <CustomIcons className="menu-item-icon school" type="absence-checked" /> Présences
            </Link>
          </List.Item>
        )}

        <List.Item
          className="mobile-bottom-sidebar-menu__item"
          key={'ecole'}
        >
          <Link className="menu-item-link" to={`/ecole`} onClick={() => setDrawerOpened(false)}>
            <CustomIcons className="menu-item-icon school" type="school" /> Ecole
          </Link>
        </List.Item>

        {localUser?.role == 'parent' && (
          <List.Item
            className="mobile-bottom-sidebar-menu__item"
            key={'linkstudent'}
          >
            <Link className="menu-item-link" to={`/linkstudent`} onClick={() => setDrawerOpened(false)}>
              <CustomIcons className="menu-item-icon" type="qrcode" /> QR étudiant
            </Link>
          </List.Item>
        )}
      </List>
    );
  }, [localUser?.role, selectedTab]);

  return (
    <div className={childClassNames} {...rest}>
      <TabBar
        className="mobile-tab-bar"
        tabBarPosition="bottom"
        activeKey={defaultActiveItem}
        onChange={value => {
          // open bottom side menu
          if (value.indexOf('menu') != -1) {
            openDrawer();
          } else {
            changeTab(value);
          }
        }}
      >
        {communicationItem}
        {absencesItem}
        {coursItem}
        {presencesItem}
        {schoolItem}
        {moreItems}
      </TabBar>
      <Drawer
        key="tcf-bottom-mobile-drawer"
        className={`tcf-bottom-mobile-drawer ${localUser?.role}`}
        style={{
          minHeight: document.documentElement.clientHeight,
        }}
        contentStyle={{
          color: '#A6A6A6',
          textAlign: 'center',
          paddingTop: 42,
        }}
        sidebar={sideContent}
        open={drawerOpened}
        onClose={openDrawer}
        position={'right'}
        touch={true}
        maskClosable={true}
        docked={false}
        closable={false}
      >
        {sideContent}
      </Drawer>
    </div>
  );
}

MobileBottomBar.propTypes = {
  selectedTab: PropTypes.string,
  changeTab: PropTypes.func.isRequired,
};

export default compose(
  memo,
  withUser,
)(MobileBottomBar);
